import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Textarea, Select, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, Table, Thead, Tbody, Tr, Th, Td, IconButton,
} from '@chakra-ui/react';
import { FiPlus, FiCheck, FiX, FiDollarSign } from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const STATUS_COLORS = { draft: 'gray', submitted: 'blue', approved: 'green', rejected: 'red', paid: 'teal' };

export default function MileageModal({ projectId, taskId, isOpen, onClose }) {
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [currentRate, setCurrentRate] = useState(0.45);
  const [allUsers, setAllUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    vehicle_type: 'personal', start_address: '', end_address: '',
    start_maps_url: '', end_maps_url: '', miles: '',
    reason: '', claim_date: new Date().toISOString().split('T')[0], user_id: '',
  });
  const toast = useToast();
  const isAccounts = user?.role === 'admin' || user?.role === 'accounts_manager';

  const fetchClaims = async () => {
    try {
      const params = taskId ? `task_id=${taskId}` : `project_id=${projectId}`;
      const { data } = await api.get(`/mileage/claims?${params}`);
      setClaims(data.data || []);
    } catch(e) {}
  };

  useEffect(() => {
    if (isOpen) {
      fetchClaims(); setShowForm(false);
      api.get('/mileage/rates/current').then(r => setCurrentRate(parseFloat(r.data.rate_per_mile))).catch(() => {});
      api.get('/calendar/users').then(r => setAllUsers(r.data)).catch(() => {});
    }
  }, [isOpen, projectId, taskId]);

  const handleSave = async () => {
    if (!form.miles || parseFloat(form.miles) <= 0) { toast({ title: 'Miles required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      await api.post('/mileage/claims', {
        project_id: projectId, task_id: taskId,
        user_id: form.user_id || user.id,
        vehicle_type: form.vehicle_type, start_address: form.start_address, end_address: form.end_address,
        start_maps_url: form.start_maps_url, end_maps_url: form.end_maps_url,
        miles: form.miles, reason: form.reason, claim_date: form.claim_date,
      });
      toast({ title: 'Mileage logged', status: 'success', duration: 1500 });
      setShowForm(false); await fetchClaims();
      setForm({ vehicle_type: 'personal', start_address: '', end_address: '', start_maps_url: '', end_maps_url: '',
        miles: '', reason: '', claim_date: new Date().toISOString().split('T')[0], user_id: '' });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const handleApprove = async (id, approved) => {
    try {
      await api.post(`/mileage/claims/${id}/approve`, { approved });
      toast({ title: approved ? 'Approved' : 'Rejected', status: approved ? 'success' : 'info', duration: 1500 });
      await fetchClaims();
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
  };

  const handleCreateExpense = async (id) => {
    try {
      await api.post(`/mileage/claims/${id}/create-expense`);
      toast({ title: 'Expense invoice created', status: 'success', duration: 2000 });
      await fetchClaims();
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
  };

  const fmtDate = (d) => d ? new Date(d.split('T')[0] + 'T12:00:00').toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader>
          <Flex justify="space-between" pr={8}>
            Mileage
            <HStack spacing={1}>
              <Badge colorScheme="blue" fontSize="10px">Rate: £{currentRate}/mile</Badge>
              <Button size="xs" leftIcon={<FiPlus />} colorScheme="blue" variant="outline"
                onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'Log Mileage'}</Button>
            </HStack>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          {showForm && (
            <Box bg="blue.50" p={3} borderRadius="md" mb={4}>
              <VStack spacing={2}>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Date</FormLabel>
                    <Input size="sm" type="date" value={form.claim_date} onChange={e => setForm(p => ({ ...p, claim_date: e.target.value }))} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Miles</FormLabel>
                    <Input size="sm" type="number" step="0.1" value={form.miles} placeholder="0.0"
                      onChange={e => setForm(p => ({ ...p, miles: e.target.value }))} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Vehicle</FormLabel>
                    <Select size="sm" value={form.vehicle_type} onChange={e => setForm(p => ({ ...p, vehicle_type: e.target.value }))}>
                      <option value="personal">Personal Vehicle</option>
                      <option value="company">Company Vehicle</option>
                    </Select></FormControl>
                </HStack>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Claimant</FormLabel>
                    <Select size="sm" value={form.user_id} onChange={e => setForm(p => ({ ...p, user_id: e.target.value }))}>
                      <option value="">Me ({user?.display_name})</option>
                      {allUsers.filter(u => u.id !== user?.id).map(u => <option key={u.id} value={u.id}>{u.display_name || u.username}</option>)}
                    </Select></FormControl>
                  <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Reason</FormLabel>
                    <Input size="sm" value={form.reason} placeholder="Purpose of travel" onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} /></FormControl>
                </HStack>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Start Address</FormLabel>
                    <Textarea size="sm" rows={2} value={form.start_address} onChange={e => setForm(p => ({ ...p, start_address: e.target.value }))} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Destination Address</FormLabel>
                    <Textarea size="sm" rows={2} value={form.end_address} onChange={e => setForm(p => ({ ...p, end_address: e.target.value }))} /></FormControl>
                </HStack>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Maps Start URL (optional)</FormLabel>
                    <Input size="sm" value={form.start_maps_url} placeholder="https://maps.google.com/..." onChange={e => setForm(p => ({ ...p, start_maps_url: e.target.value }))} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Maps End URL (optional)</FormLabel>
                    <Input size="sm" value={form.end_maps_url} placeholder="https://maps.google.com/..." onChange={e => setForm(p => ({ ...p, end_maps_url: e.target.value }))} /></FormControl>
                </HStack>
                {form.miles && (
                  <Text fontSize="12px" fontWeight="700" color="blue.600" alignSelf="flex-end">
                    Estimated: £{(parseFloat(form.miles || 0) * currentRate).toFixed(2)}
                  </Text>
                )}
                <Button size="xs" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                  onClick={handleSave} isLoading={saving} alignSelf="flex-end">Save Claim</Button>
              </VStack>
            </Box>
          )}

          {claims.length === 0 ? (
            <Text color="gray.500" textAlign="center" py={6}>No mileage claims logged.</Text>
          ) : (
            <Table size="sm"><Thead><Tr>
              <Th px={2}>Date</Th><Th px={2}>Claimant</Th><Th px={2}>Miles</Th><Th px={2}>Amount</Th>
              <Th px={2}>Route</Th><Th px={2}>Status</Th>{isAccounts && <Th px={2}>Actions</Th>}
            </Tr></Thead><Tbody>
              {claims.map(c => (
                <Tr key={c.id} _hover={{ bg:'gray.50' }}>
                  <Td px={2}>{fmtDate(c.claim_date)}</Td>
                  <Td px={2}>{c.user_name || c.created_by_name || '—'}</Td>
                  <Td px={2} fontWeight="600">{parseFloat(c.miles).toFixed(1)}</Td>
                  <Td px={2} fontWeight="600" color="blue.600">£{parseFloat(c.total_amount).toFixed(2)}</Td>
                  <Td px={2} maxW="200px" isTruncated>
                    {c.start_address && c.end_address ? `${c.start_address} → ${c.end_address}` : c.reason || '—'}
                  </Td>
                  <Td px={2}><Badge colorScheme={STATUS_COLORS[c.status]}>{c.status}</Badge>
                    {c.payment_reference && <Text fontSize="9px" color="gray.500">{c.payment_reference}</Text>}
                  </Td>
                  {isAccounts && (
                    <Td px={2}>
                      <HStack spacing={0}>
                        {c.status === 'draft' && (
                          <>
                            <IconButton icon={<FiCheck size={14} />} size="xs" variant="ghost" colorScheme="green"
                              onClick={() => handleApprove(c.id, true)} aria-label="Approve" />
                            <IconButton icon={<FiX size={14} />} size="xs" variant="ghost" colorScheme="red"
                              onClick={() => handleApprove(c.id, false)} aria-label="Reject" />
                          </>
                        )}
                        {c.status === 'approved' && !c.invoice_id && (
                          <IconButton icon={<FiDollarSign size={14} />} size="xs" variant="ghost" colorScheme="orange"
                            onClick={() => handleCreateExpense(c.id)} aria-label="Create Expense" />
                        )}
                      </HStack>
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody></Table>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}
